import { FlatIconButton } from "@dgs/core";
import { HTMLAttributes, Ref, forwardRef } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

export interface MenuTreeNodeProps extends Omit<HTMLAttributes<HTMLDivElement>, "id"> {
	childCount?: number;
	clone?: boolean;
	ghost?: boolean;
	depth: number;
	handleProps?: any;
	value: string;
	onRemove?: () => void;
	onEdit?: () => void;
	wrapperRef?(node: HTMLDivElement): void;
}

interface WrapperProps {
	$depth: number;
	$clone: boolean;
}

const Wrapper = styled.div<WrapperProps>`
	${({ theme }) => theme.borderRadius.medium};
	border: 1px solid ${({ theme }) => theme.colors.palette.grey[40]};
	background-color: ${({ theme }) => theme.colors.palette.white};
	overflow: hidden;
	box-shadow: ${({ theme }) => theme.shadows.l1};
	padding: ${({ theme }) => theme.spacing(2)} ${({ theme }) => theme.spacing(4)};
	gap: ${({ theme }) => theme.spacing(2)};
	margin-left: ${({ $clone, $depth, theme }) => ($clone ? "0" : theme.spacing($depth * 10))};
`;

const Content = styled.div`
	display: grid;
	grid-template-columns: max-content 1fr max-content max-content;
	justify-content: center;
	align-items: center;
	gap: ${({ theme }) => theme.spacing(2)};
	width: 100%;
`;

const Count = styled.span`
	position: absolute;
	top: -10px;
	right: -10px;
	display: flex;
	align-items: center;
	justify-content: center;
	width: 24px;
	height: 24px;
	border-radius: 50%;
	background-color: ${({ theme }) => theme.colors.palette.primary.main.base};
	font-size: ${({ theme }) => theme.fonts.sizes.small};
	font-weight: ${({ theme }) => theme.fonts.weights.bold};
	color: ${({ theme }) => theme.colors.palette.primary.main.contrast};
`;

export const PreferencesTreeNode = forwardRef<HTMLDivElement, MenuTreeNodeProps>(function TreeNode(
	{ childCount, clone, depth, handleProps, onRemove, onEdit, value, style, wrapperRef, ...props },
	ref: Ref<HTMLDivElement>,
) {
	const { t } = useTranslation();

	return (
		<Wrapper $clone={!!clone} $depth={depth} ref={wrapperRef} {...props} style={style}>
			<Content ref={ref}>
				<FlatIconButton icon="drag_indicator" {...handleProps} />
				<span>{value}</span>
				{!clone && <FlatIconButton size="small" icon="edit" type="button" onClick={onEdit} title={t("Edit")} />}
				{!clone && <FlatIconButton size="small" icon="delete" type="button" onClick={onRemove} title={t("Remove")} />}
				{clone && childCount && childCount > 1 ? <Count>{childCount}</Count> : null}
			</Content>
		</Wrapper>
	);
});
